import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import {
    User,
    Mail,
    Phone,
    Home,
    MapPin,
    Calendar,
    Edit2,
    Save,
    X,
    Loader2,
    AlertCircle,
    Check,
    Camera,
    LogOut,
    ChevronRight,
    FileText,
    CreditCard,
    Wrench
} from 'lucide-react';
import { motion } from 'framer-motion';

interface TenancyDetails {
    property_name: string;
    unit_number: string;
    lease_start_date: string;
    property_address: string;
    rent_amount: number;
    status: string;
}

interface ProfileFormData {
    fullName: string;
    phoneNumber: string;
    bio: string;
}

export const TenantProfile: React.FC = () => {
    const { profile, user, refreshProfile, signOut } = useAuth();
    const navigate = useNavigate();
    const { success, error: toastError } = useToast();

    const [tenancyDetails, setTenancyDetails] = useState<TenancyDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const [formData, setFormData] = useState<ProfileFormData>({
        fullName: '',
        phoneNumber: '',
        bio: '',
    });

    useEffect(() => {
        fetchTenancyDetails();
        if (profile) {
            setFormData({
                fullName: profile.full_name || '',
                phoneNumber: profile.phone_number || '',
                bio: profile.bio || '',
            });
        }
    }, [profile]);

    const fetchTenancyDetails = async () => {
        setIsLoading(true);
        try {
            const response = await apiClient.get('/tenants/my-tenancy');
            setTenancyDetails(response.data);
        } catch (err: any) {
            console.error('Failed to fetch tenancy details:', err);
            toastError('Error', 'Could not load tenancy details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await apiClient.put('/profile', {
                full_name: formData.fullName,
                phone_number: formData.phoneNumber,
                bio: formData.bio,
            });
            await refreshProfile();
            success('Profile updated successfully!');
            setIsEditing(false);
        } catch (err: any) {
            toastError('Error', err.response?.data?.message || 'Failed to update profile');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAvatarClick = () => {
        // Navigate to main profile page for avatar upload
        navigate('/profile');
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch {
            toastError('Error', 'Failed to sign out');
        }
    };

    const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Tenant';
    const initials = displayName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);

    const quickActions = [
        {
            icon: <CreditCard size={18} />,
            label: 'Make Payment',
            description: 'Pay rent and view transactions',
            onClick: () => navigate('/tenant/payments'),
            accent: '#10B981',
        },
        {
            icon: <Wrench size={18} />,
            label: 'Request Fix',
            description: 'Submit maintenance requests',
            onClick: () => navigate('/tenant/maintenance'),
            accent: '#F59E0B',
        },
        {
            icon: <FileText size={18} />,
            label: 'Documents',
            description: 'View lease and agreements',
            onClick: () => navigate('/tenant/documents'),
            accent: '#3B82F6',
        },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 size={40} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-light-card dark:bg-dark-card rounded-2xl overflow-hidden border border-light-border dark:border-dark-border"
            >
                {/* Gradient Banner */}
                <div className="h-32 bg-gradient-to-br from-primary via-primary/90 to-[#0EA5E9]/70 dark:from-[#0c0c0c] dark:via-[#111] dark:to-primary/20 relative">
                    <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-[#0EA5E9]/20 blur-xl" />
                    {!isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/20 backdrop-blur-sm text-white text-xs font-semibold hover:bg-white/30 transition-colors"
                        >
                            <Edit2 size={12} /> Edit Profile
                        </button>
                    )}
                </div>

                {/* Avatar + Info */}
                <div className="px-6 pb-6">
                    <div className="flex items-end gap-4 -mt-12 mb-4">
                        {/* Avatar */}
                        <div
                            onClick={handleAvatarClick}
                            className="relative flex-shrink-0 cursor-pointer group"
                        >
                            <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-light-card dark:border-dark-card shadow-lg bg-gradient-to-br from-primary to-[#0EA5E9]">
                                {profile?.avatar_url ? (
                                    <img
                                        src={profile.avatar_url}
                                        alt={displayName}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <span className="text-white font-black text-2xl">{initials}</span>
                                    </div>
                                )}
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                                <Camera size={14} className="text-white" />
                            </div>
                        </div>

                        {/* Name + Email */}
                        <div className="flex-1 pt-12">
                            <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">
                                {displayName}
                            </h1>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                {user?.email}
                            </p>
                            {tenancyDetails && (
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                        {tenancyDetails.status || 'Active'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-3 gap-3 mt-4">
                        {quickActions.map((action, index) => (
                            <button
                                key={index}
                                onClick={action.onClick}
                                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border hover:border-primary/30 hover:bg-primary/5 transition-all group"
                            >
                                <div
                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: `${action.accent}15` }}
                                >
                                    <span style={{ color: action.accent }}>{action.icon}</span>
                                </div>
                                <span className="text-xs font-medium text-light-text dark:text-dark-text text-center">
                                    {action.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Profile Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Contact Information Card */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-light-card dark:bg-dark-card rounded-2xl border border-light-border dark:border-dark-border overflow-hidden"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-light-border dark:border-dark-border">
                            <div className="flex items-center gap-2">
                                <div className="w-1 h-4 rounded-full bg-primary" />
                                <span className="text-sm font-bold text-light-text dark:text-dark-text">
                                    Contact Information
                                </span>
                            </div>
                            {!isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-75 transition-opacity"
                                >
                                    <Edit2 size={12} /> Edit
                                </button>
                            )}
                        </div>

                        {isEditing ? (
                            <form onSubmit={handleEditSubmit} className="p-5 space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) =>
                                            setFormData({ ...formData, fullName: e.target.value })
                                        }
                                        className="w-full px-3.5 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phoneNumber}
                                        onChange={(e) =>
                                            setFormData({ ...formData, phoneNumber: e.target.value })
                                        }
                                        placeholder="+254 700 000 000"
                                        className="w-full px-3.5 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">
                                        Bio
                                    </label>
                                    <textarea
                                        value={formData.bio}
                                        onChange={(e) =>
                                            setFormData({ ...formData, bio: e.target.value })
                                        }
                                        rows={3}
                                        className="w-full px-3.5 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                                    />
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                                    >
                                        {isSaving ? (
                                            <Loader2 size={15} className="animate-spin" />
                                        ) : (
                                            <Save size={15} />
                                        )}
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditing(false);
                                            if (profile) {
                                                setFormData({
                                                    fullName: profile.full_name || '',
                                                    phoneNumber: profile.phone_number || '',
                                                    bio: profile.bio || '',
                                                });
                                            }
                                        }}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm font-semibold text-light-text dark:text-dark-text hover:opacity-80 transition-opacity"
                                    >
                                        <X size={15} /> Cancel
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="p-5 space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Mail size={14} className="text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                            Email
                                        </p>
                                        <p className="text-sm text-light-text dark:text-dark-text">
                                            {user?.email}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Phone size={14} className="text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                            Phone
                                        </p>
                                        <p className="text-sm text-light-text dark:text-dark-text">
                                            {profile?.phone_number || 'Not provided'}
                                        </p>
                                    </div>
                                </div>
                                {profile?.bio && (
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <User size={14} className="text-primary" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                                About
                                            </p>
                                            <p className="text-sm text-light-text dark:text-dark-text">
                                                {profile.bio}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>

                    {/* Account Actions */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-light-card dark:bg-dark-card rounded-2xl border border-light-border dark:border-dark-border overflow-hidden"
                    >
                        <div className="flex items-center gap-2 px-5 py-4 border-b border-light-border dark:border-dark-border">
                            <div className="w-1 h-4 rounded-full bg-light-subtle dark:bg-dark-subtle" />
                            <span className="text-sm font-bold text-light-text dark:text-dark-text">
                                Account
                            </span>
                        </div>
                        <div className="divide-y divide-light-border dark:divide-[#1e1e1e]">
                            <button
                                onClick={() => navigate('/profile')}
                                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors group text-left"
                            >
                                <User size={17} className="text-light-subtle dark:text-dark-subtle" />
                                <span className="flex-1 text-sm font-medium text-light-text dark:text-dark-text">
                                    Full Profile Settings
                                </span>
                                <ChevronRight size={15} className="text-light-subtle dark:text-dark-subtle group-hover:translate-x-0.5 transition-transform" />
                            </button>
                            <button
                                onClick={handleSignOut}
                                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-danger/5 transition-colors group text-left"
                            >
                                <LogOut size={17} className="text-danger" />
                                <span className="flex-1 text-sm font-medium text-danger">
                                    Sign Out
                                </span>
                                <ChevronRight size={15} className="text-danger group-hover:translate-x-0.5 transition-transform" />
                            </button>
                        </div>
                    </motion.div>
                </div>

                {/* Right Column - Tenancy Details */}
                <div className="space-y-6">
                    {tenancyDetails && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="bg-light-card dark:bg-dark-card rounded-2xl border border-light-border dark:border-dark-border overflow-hidden"
                        >
                            <div className="flex items-center gap-2 px-5 py-4 border-b border-light-border dark:border-dark-border">
                                <div className="w-1 h-4 rounded-full bg-[#0EA5E9]" />
                                <span className="text-sm font-bold text-light-text dark:text-dark-text">
                                    Tenancy Details
                                </span>
                            </div>
                            <div className="p-5 space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Home size={14} className="text-[#0EA5E9]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                            Property
                                        </p>
                                        <p className="text-sm text-light-text dark:text-dark-text">
                                            {tenancyDetails.property_name}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <MapPin size={14} className="text-[#0EA5E9]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                            Unit
                                        </p>
                                        <p className="text-sm text-light-text dark:text-dark-text">
                                            {tenancyDetails.unit_number}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <MapPin size={14} className="text-[#0EA5E9]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                            Address
                                        </p>
                                        <p className="text-sm text-light-text dark:text-dark-text">
                                            {tenancyDetails.property_address}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Calendar size={14} className="text-[#0EA5E9]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                            Move-in Date
                                        </p>
                                        <p className="text-sm text-light-text dark:text-dark-text">
                                            {new Date(
                                                tenancyDetails.lease_start_date
                                            ).toLocaleDateString('en-KE', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                            })}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <CreditCard size={14} className="text-[#0EA5E9]" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">
                                            Monthly Rent
                                        </p>
                                        <p className="text-sm text-light-text dark:text-dark-text font-semibold">
                                            KES {tenancyDetails.rent_amount?.toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Help Card */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-gradient-to-br from-primary/10 to-[#0EA5E9]/10 dark:from-primary/20 dark:to-[#0EA5E9]/20 rounded-2xl border border-primary/20 p-5"
                    >
                        <div className="flex items-start gap-3">
                            <AlertCircle size={20} className="text-primary flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-bold text-light-text dark:text-dark-text mb-1">
                                    Need Help?
                                </h3>
                                <p className="text-xs text-light-subtle dark:text-dark-subtle">
                                    Contact your landlord for any issues related to your tenancy.
                                </p>
                                <button className="mt-3 text-xs font-semibold text-primary hover:underline">
                                    Contact Support →
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};